import { useState } from "react";
import { X, Check, AlertTriangle, Loader2 } from "lucide-react";
import { api } from "../../../helpers/api";

interface PromoValidatorModalProps {
  onClose: () => void;
}

// Per-scope outcome. "error" (a transport/auth/server failure) is kept distinct
// from "invalid" (the endpoint answered "not valid") — otherwise a failed
// request would masquerade as a Stripe misconfiguration and mislead CS.
type ScopeState =
  | { status: "valid"; rate?: number }
  | { status: "invalid" }
  | { status: "error" };

const pct = (r: number) => `${+(r * 100).toFixed(2)}%`;

export function PromoValidatorModal({ onClose }: PromoValidatorModalProps) {
  const [code, setCode] = useState("");
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<{
    code: string;
    pricing: ScopeState;
    marketing: ScopeState;
  } | null>(null);

  const check = async () => {
    const c = code.trim();
    if (!c) return;
    setChecking(true);
    setResult(null);
    // allSettled so a failure in one scope can't be mistaken for "not valid".
    const [p, m] = await Promise.allSettled([
      api.resolvePromoCode(c, "pricing"),
      api.resolvePromoCode(c, "marketing"),
    ]);
    const toState = (
      r: PromiseSettledResult<{ valid: boolean; commissionRate?: number }>,
    ): ScopeState =>
      r.status === "rejected"
        ? { status: "error" }
        : r.value.valid
          ? { status: "valid", rate: r.value.commissionRate }
          : { status: "invalid" };
    setResult({ code: c, pricing: toState(p), marketing: toState(m) });
    setChecking(false);
  };

  const erroredScopes = result
    ? [
        result.pricing.status === "error" ? "pricing" : null,
        result.marketing.status === "error" ? "marketing" : null,
      ].filter(Boolean)
    : [];
  const bothInvalid =
    result &&
    result.pricing.status === "invalid" &&
    result.marketing.status === "invalid";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface-primary rounded-2xl shadow-2xl w-full max-w-md flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-secondary">
          <div>
            <h3 className="text-base font-semibold text-content-primary">
              Validate promo code
            </h3>
            <p className="text-xs text-content-tertiary mt-0.5">
              Checks it exactly like the signup form — a green result means it
              will redeem.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-content-tertiary hover:text-content-primary hover:bg-surface-secondary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="flex gap-2">
            <input
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && check()}
              placeholder="e.g. MADEIRA"
              className="flex-1 px-3 py-2 text-sm border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-surface-primary"
            />
            <button
              onClick={check}
              disabled={checking || !code.trim()}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary-hover disabled:opacity-50 flex items-center gap-1.5"
            >
              {checking && <Loader2 className="w-4 h-4 animate-spin" />}
              {checking ? "Checking…" : "Check"}
            </button>
          </div>

          {result && (
            <div className="space-y-2 text-sm">
              {result.pricing.status === "valid" && (
                <div className="flex items-center gap-2 text-success">
                  <Check className="w-4 h-4 shrink-0" />
                  <span>
                    Valid on <b>Pricing</b> —{" "}
                    {result.pricing.rate != null
                      ? `${pct(result.pricing.rate)} off the plan`
                      : "discount applies (rate unavailable)"}
                    .
                  </span>
                </div>
              )}
              {result.marketing.status === "valid" && (
                <div className="flex items-center gap-2 text-success">
                  <Check className="w-4 h-4 shrink-0" />
                  <span>
                    Valid on <b>Marketing</b> —{" "}
                    {result.marketing.rate != null
                      ? `${pct(result.marketing.rate)} commission`
                      : "commission applies (rate unavailable)"}
                    .
                  </span>
                </div>
              )}

              {erroredScopes.length > 0 && (
                <div className="flex items-start gap-2 text-warning-dark">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    Couldn't check <b>{erroredScopes.join(" & ")}</b> — the
                    request failed (session expired or server error).{" "}
                    <b>Not a verdict on the code.</b> Try again.
                  </span>
                </div>
              )}

              {bothInvalid && (
                <div className="flex items-start gap-2 text-error">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    <b>“{result.code}” is not usable on either field.</b> Most
                    likely there's no <b>Promotion Code</b> in Stripe for it (a
                    coupon alone can't be redeemed), or it's inactive/expired, or
                    it's an amount-off coupon. Create/enable the promotion code in
                    Stripe and re-check.
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
